import clamp from 'lodash/clamp';
import range from 'lodash/range';
import {MouseEvent, useCallback, useEffect, useRef, useState} from 'react';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {DialogStarProps} from './DialogStarProps.interface';

export function DialogStar({rating = 10, onChange}: DialogStarProps) {
  const [innerRatingRatio, setInnerRatingRatio] = useState<number>(rating);
  const isDraggable = useRef<boolean>(false);
  const starWrapperRef = useRef<HTMLDivElement>(null);
  const starScrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onChange && onChange(innerRatingRatio);
  }, [innerRatingRatio, onChange]);

  const handleRatingChange = useCallback(
    (
      e:
        | globalThis.TouchEvent
        | globalThis.MouseEvent
        | MouseEvent<HTMLDivElement, globalThis.MouseEvent>
    ) => {
      if (!starWrapperRef.current || !isDraggable.current) return;

      const isTouch = 'touches' in e;
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const {x: wrapperLeft} = starWrapperRef.current.getBoundingClientRect();

      const ratingRatio = clamp(
        Math.round(((clientX - wrapperLeft) / STARS_WIDTH) * 10),
        1,
        10
      );

      setInnerRatingRatio(ratingRatio);
    },
    []
  );

  // Dialog 밖에서 스크롤을 전파를 막는 useStopPropagationOnScroll 를 사용하고 있습니다.
  // React 이벤트는 addEventListener보다 나중에 붙으므로, Dom 구조에 맞게 이벤트를 붙히기 위해 addEventListener로 이벤트를 달아줍니다.
  useEffect(() => {
    const scrollableEl = starScrollableRef.current;
    if (!scrollableEl) return;

    function setDraggableTrue() {
      isDraggable.current = true;
    }
    function setDraggableFalse() {
      isDraggable.current = false;
    }

    scrollableEl.addEventListener('mousedown', setDraggableTrue);
    window.addEventListener('mousemove', handleRatingChange);
    window.addEventListener('mouseup', setDraggableFalse);

    scrollableEl.addEventListener('touchstart', setDraggableTrue);
    scrollableEl.addEventListener('touchmove', handleRatingChange);
    scrollableEl.addEventListener('touchend', setDraggableFalse);

    return () => {
      scrollableEl.removeEventListener('mousedown', setDraggableTrue);
      window.removeEventListener('mousemove', handleRatingChange);
      window.removeEventListener('mouseup', setDraggableFalse);

      scrollableEl.removeEventListener('touchstart', setDraggableTrue);
      scrollableEl.removeEventListener('touchmove', handleRatingChange);
      scrollableEl.removeEventListener('touchend', setDraggableFalse);
    };
  }, [handleRatingChange]);

  return (
    <div className="select-none">
      <div
        className="flex cursor-pointer items-center justify-center"
        onClick={e => {
          e.stopPropagation();
          isDraggable.current = true;
          handleRatingChange(e);
          isDraggable.current = false;
        }}
        ref={starScrollableRef}
      >
        <div
          ref={starWrapperRef}
          className="relative inline-flex overflow-hidden"
        >
          {range(0, 5).map(index => {
            return (
              <Icon
                key={index}
                type={
                  STAR_ICON_TYPES[
                    //첫 번째 별점은 최소 반개부터 시작해야함. 점수 1~10점
                    clamp(rating - index * 2, index === 0 ? 1 : 0, 2)
                  ]
                }
                className="pointer-events-none"
              />
            );
          })}
        </div>
      </div>
      <div className="mt-20pxr flex items-center justify-center">
        <Text className="font-medium1-bold text-theme-solid-100">
          {clamp(rating, 1, 10)}
        </Text>
      </div>
    </div>
  );
}

const STARS_WIDTH = 140;
const STAR_ICON_TYPES = [
  IconType.ViewerStarOff,
  IconType.ViewerStarHalf,
  IconType.ViewerStarOn,
];
export const DialogStarType = (<DialogStar />).type;
